import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [smsEnabled, setSmsEnabled] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState('');

  React.useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.data();
          setSmsEnabled(userData?.smsFactorEnabled || false);
          setPhoneNumber(userData?.phoneNumber || '');
        } catch (error) {
          console.error('Error loading user data:', error);
          // Fail silently - component will show default disabled state
        }
      }
    };
    loadUserData();
  }, [user]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Security Settings
        </h1>
        <p className="text-gray-600">
          Manage your account security and two-factor authentication
        </p>
      </div>

      {/* SMS Two-Factor Authentication Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              SMS Two-Factor Authentication
            </h2>
            <p className="text-gray-600 mb-4">
              Secure your account with text message verification codes
            </p>

            {smsEnabled ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="font-semibold text-green-900">
                    SMS 2FA Enabled
                  </span>
                </div>
                <p className="text-sm text-green-700">Phone: {phoneNumber}</p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    className="w-5 h-5 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="font-semibold text-yellow-900">
                    2FA Not Enabled
                  </span>
                </div>
                <p className="text-sm text-yellow-700">
                  Enable SMS authentication to add an extra layer of security
                </p>
              </div>
            )}

            <button
              onClick={() => navigate('/setup-sms-2fa')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {smsEnabled ? 'Update Phone Number' : 'Enable SMS 2FA'}
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3">How it works:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>
                When you sign in, we'll send a verification code to your phone
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>Enter the code to complete your login</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>Codes expire after 5 minutes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>Make sure your phone can receive SMS messages</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;
